<template>
  <div class="ts" :class="{ 'ts-cols': mode !== 'compact' }">
    <section v-for="tm in list" v-show="!tm.hidden" :key="tm.key" :class="['ts-panel', 'team', tm.key]" :style="{ '--tc': tm.bright }">
      <div class="ts-head">
        <button class="ts-name" @click="$emit('team', tm.key)" v-tip="'Open the ' + tm.label + ' team card'">{{ tm.label }} <span class="ts-more">Team card ›</span></button>
        <span v-if="tm.units.length && gameData" class="lx-grade ts-grade" :style="{ background: teamG(tm).color }" v-tip="() => teamTip(tm.label, tm.units, { color: tm.bright, tg: teamG(tm) })">{{ teamG(tm).grade }}</span>
        <ChampStreakBadge v-if="tm.key === 'champion' && champStreak" :streak="champStreak" />
        <span class="ts-fate">{{ tm.fate }}</span>
      </div>
      <div v-if="mode !== 'compact' && gameData && tm.units.length" class="ts-full">
        <UnitFullPanel v-for="(u, k) in tm.units" :key="k" :data-player="(u.Name || '').toLowerCase()" :class="{ boss: u.RaidBoss }"
                       :unit="u" :side="tm" :foe="tm.foe" :gd="gameData" :fallback="portrait(u, tm.label)" :name-color="trackColor(u)"
                       @open="card = { unit: u, side: tm, foe: tm.foe }" />
      </div>
      <button v-else v-for="(u, k) in tm.units" :key="k" class="ts-row" :data-player="(u.Name || '').toLowerCase()" :class="{ boss: u.RaidBoss }"
              @click="card = { unit: u, side: tm, foe: tm.foe }" v-tip="() => unitTip(u, { team: tm.label, color: tm.bright, grade: gameData ? unitG(u) : null, gd: gameData, foes: tm.foe && tm.foe.units, foeLabel: tm.foe && tm.foe.label, mates: tm.units, foot: 'Click for the full card' })">
        <UnitSprite :unit="u" :team-color="tm.pal" :fallback-src="portrait(u, tm.label)" :size="58" />
        <span v-if="gameData" class="lx-grade ts-g" :style="{ background: unitG(u).color }">{{ unitG(u).grade }}</span>
        <span class="ts-who">
          <b :style="trackColor(u) ? { color: trackColor(u) } : null"><span v-if="u.RaidBoss" class="ts-boss" v-tip="'Raid boss'">★</span>{{ u.Name }}<small v-if="u.Npc" class="lx-npc" v-tip="NPC_TIP">NPC</small></b>
          <i>{{ u.Gender === 'Monster' ? '' : u.Gender + ' ' }}{{ u.Class }} · {{ u.Sign }}</i>
        </span>
        <span class="ts-nums"><i>HP</i> {{ stat(u, 'hp') }} <i>MP</i> {{ stat(u, 'mp') }}</span>
        <span class="ts-line ts-stats">
          <span v-for="[k, l] in STAT_COLS" :key="k" v-tip="endlessTip(k, stat(u, k), u)"><i>{{ l }}</i> {{ fmtStat(k, stat(u, k)) }}</span>
          <span><i>Brave</i> {{ u.Brave }}</span><span><i>Faith</i> {{ u.Faith }}</span>
          <span v-if="info(u)" v-tip="'Chance to dodge a physical hit from the front / side / back, and a spell'"><i>Evade</i> {{ info(u).evade.front }}/{{ info(u).evade.side }}/{{ info(u).evade.back }} <i>Mag</i> {{ info(u).evade.magic }}</span>
        </span>
        <KitLine class="ts-line ts-kit" :unit="u" split />
        <span v-if="gearFx(u)" class="ts-line ts-fx">{{ gearFx(u) }}</span>
        <span v-if="skills(u).length" class="ts-line ts-skills">
          <span v-for="sk in skills(u)" :key="sk.name" class="ts-sk" :style="wrStyleFor(u, 'Ability', sk.name)"
                v-tip="[sk.tier ? sk.tier.label + ' · rolls ' + sk.roll + '%' : '', wrTipFor(u, 'Ability', sk.name)].filter(Boolean).join(' · ')">{{ sk.name }}<i v-if="sk.tier" class="ts-rar" :style="{ background: sk.tier.color }">{{ sk.roll }}%</i></span>
        </span>
      </button>
      <p v-if="!tm.units.length" class="ts-empty">No roster yet.</p>
    </section>
    <UnitCardNew v-if="card && gameData" :unit="card.unit" :side="card.side" :foe="card.foe" :gd="gameData" :fallback="portrait(card.unit, card.side.label)"
                 @close="card = null" />
  </div>
</template>

<script>
// The homepage's team section (2026-10 refresh), under the bracket: every team in a brass-framed panel with its grade
// and how its tournament is going ("Lost the quarterfinal to Blue", "Next: the semifinal against Yellow"), each unit a
// row with its grade, class, sign, stats, evasion, kit, what its gear does (naming the piece) and its skills with the rare
// ones' roll % (2026-10-24, what the old team cards showed). Click a unit for its full card (UnitCardNew, against
// the team it faces next, or last faced). The team's
// name opens its full team card (`team`). By default (`mode` 'columns', prefs.teamCards, since 2026-10-26: chat missed the
// old site's expanded cards) each team is a full-width row of four full cards (UnitFullPanel); 'compact' is the rows. Panels keep the old grid's hooks: `.team.<name>` (Luxstadio's goto) and each row's
// `data-player` (gotoPlayer), and a team's `hidden` flag (the team buttons) hides its panel.
import UnitSprite from '../UnitSprite.vue';
import ChampStreakBadge from '../ChampStreakBadge.vue';
import UnitCardNew from '../spotlight/UnitCardNew.vue';
import { unitInfo, gearEffectsLine, skillSets } from '../spotlight/unitInfo.js';
import { matchColors } from '../../lib/spriteForClass.js';
import { teamBright } from '../../lib/teamColors.js';
import { UNITS_BASE } from '../../lib/assetUrls.js';
import { unitGrade, teamGrade } from '../../lib/unitGrade.js';
import { rosterStats } from '../../lib/preload.js';
import { unitTip, teamTip } from '../../lib/unitTip.js';
import { teamPath } from './teamPath.js';
import { fmtStat, endlessTip } from '../../lib/statDisplay.js';
import { wrStyleFor, wrTipFor } from '../../lib/wrPref.js';
import KitLine from '../spotlight/KitLine.vue';
import { NPC_TIP } from '../../lib/fftbgLive.js';
import UnitFullPanel from './UnitFullPanel.vue';

const STAT_COLS = [['speed', 'Spd'], ['move', 'Mv'], ['jump', 'Jp'], ['pa', 'PA'], ['ma', 'MA']];

export default {
  name: 'TeamSection',
  components: { UnitSprite, ChampStreakBadge, UnitCardNew, KitLine, UnitFullPanel },
  props: {
    teams: { type: Array, required: true },      // [{ name, label, roster, palettes, hidden }]
    winners: { type: Array, default: () => [] },
    gameData: { type: Object, default: null },
    champStreak: { type: Number, default: 0 },
    highlightColors: { type: [Map, Object], default: () => new Map() },
    mode: { type: String, default: 'columns' }, // 'columns' | 'compact' (prefs.teamCards)
  },
  emits: ['team'],
  data() { return { card: null, STAT_COLS, NPC_TIP }; },
  computed: {
    byName() { const m = {}; for (const t of this.teams) m[t.name] = t; return m; },
    list() {
      const W = this.winners || [];
      const side = (t) => ({ key: t.name, label: t.label, units: t.roster || [], palettes: t.palettes, bright: teamBright(t.label) });
      const label = (n) => (n === 'champion' ? 'the Champion' : (this.byName[n] || {}).label || n);
      return this.teams.map((t) => {
        const tm = { ...side(t), hidden: t.hidden };
        [tm.pal] = matchColors(t, null);
        const path = teamPath(t.name, W, label);
        const o = path.foeKey && this.byName[path.foeKey];
        tm.foe = o ? side(o) : null;
        if (tm.foe) [tm.pal, tm.foe.pal] = matchColors(t, o);
        tm.fate = path.fate;
        return tm;
      });
    },
  },
  watch: { teams() { this._ug = null; this._tg = null; this._info = null; this._sk = null; } },
  methods: {
    portrait(u, label) {
      if (u.Gender === 'Monster') return UNITS_BASE + u.Class + '.gif';
      const g = (u.Gender || 'Male')[0];
      return label === 'Champion' ? UNITS_BASE + u.Class + g + '.gif' : UNITS_BASE + u.Class + g + '_' + label + '.gif';
    },
    info(u) { this._info = this._info || new Map(); if (!this._info.has(u)) { let r = null; try { r = unitInfo(u, this.gameData); } catch (e) { r = null; } this._info.set(u, r); } return this._info.get(u); },
    stat(u, k) { const i = this.gameData && this.info(u); return i ? i.stats[k] : '–'; },
    // what the gear does beyond numbers, naming the piece: "Starts with Regen, boosts Holy (Holy Miter)"
    gearFx(u) { const i = this.gameData && this.info(u); return i ? gearEffectsLine(u, i.equipped) : null; },
    // every action skill the unit learned, the rare ones with their roll % (as the old team cards had)
    skills(u) { this._sk = this._sk || new Map(); if (!this._sk.has(u)) this._sk.set(u, skillSets(u, this.gameData).flatMap((x) => x.skills)); return this._sk.get(u); },
    unitTip, teamTip, fmtStat, endlessTip,
    // win-rate colors (Settings' switch, the team bar's chip), shared with the spotlight via lib/wrPref.js
    wrStyleFor, wrTipFor,
    unitG(u) { this._ug = this._ug || new Map(); if (!this._ug.has(u)) this._ug.set(u, unitGrade(u, rosterStats, this.gameData)); return this._ug.get(u); },
    teamG(tm) { this._tg = this._tg || new Map(); if (!this._tg.has(tm.units)) this._tg.set(tm.units, teamGrade(tm.units, rosterStats, this.gameData)); return this._tg.get(tm.units); },
    trackColor(u) { const h = this.highlightColors, n = (u.Name || '').toLowerCase(); return (h && n && (h instanceof Map ? h.get(n) : h[n])) || null; },
  },
};
</script>

<style scoped>
.ts { display: grid; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); gap: 14px; margin: 8px 0 24px; text-align: left; }
@media (max-width: 480px) { .ts { grid-template-columns: minmax(0, 1fr); } }
.ts-cols { grid-template-columns: minmax(0, 1fr); }
.ts-full { display: grid; grid-template-columns: repeat(auto-fill, minmax(250px, 1fr)); gap: 8px; margin-top: 4px; }
.ts-panel { margin: 0; border: 1px solid var(--brass-edge); border-top: 3px solid var(--tc); border-radius: 4px; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); padding: 8px 10px; box-shadow: 0 4px 12px rgba(0,0,0,.45); color: var(--ink); scroll-margin-top: 60px; }
.ts-head { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px; }
.ts-name { padding: 0; border: 0; background: none; cursor: pointer; font-family: var(--cond); font-weight: 700; font-size: 18px; text-transform: uppercase; letter-spacing: 1px; color: var(--tc); text-shadow: 0 1px 0 #000; filter: brightness(1.3); }
.ts-name:hover .ts-more, .ts-name:focus-visible .ts-more { opacity: 1; }
.ts-more { font-size: 12px; letter-spacing: 0.6px; color: var(--brass); filter: none; opacity: 0.55; vertical-align: 2px; }
.ts-grade { font-size: 14px; }
.ts-fate { margin-left: auto; font-size: 12px; font-style: italic; color: var(--mute); text-align: right; }
.ts-row { width: 100%; display: grid; grid-template-columns: 60px 30px minmax(0, 1fr) auto; column-gap: 8px; row-gap: 1px; align-items: center; padding: 5px 0;
  background: none; border: 0; border-top: 1px solid rgba(255,255,255,.05); border-radius: 3px; font: inherit; color: inherit; text-align: left; cursor: pointer; scroll-margin-top: 80px; }
.ts-row:hover, .ts-row:focus-visible { background: rgba(217,180,94,.08); outline: none; }
.ts-row.flash { animation: ts-flash 2s ease-out; }
@keyframes ts-flash { 0%, 40% { background: rgba(243,214,142,.35); } 100% { background: none; } }
.ts-row > :first-child { grid-row: 1 / span 5; align-self: start; }
.ts-g { grid-row: 1 / span 5; align-self: start; margin-top: 18px; justify-self: center; font-size: 13px; }
.ts-who { min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ts-who b { font-family: var(--cond); font-weight: 700; font-size: 15px; color: var(--ink-hi); }
.ts-who i { font-style: normal; font-size: 12px; color: var(--mute); margin-left: 6px; }
.ts-boss { color: gold; margin-right: 2px; }
.ts-row.boss { box-shadow: inset 3px 0 0 gold; }
.ts-nums { font-size: 12.5px; white-space: nowrap; font-variant-numeric: tabular-nums; color: var(--ink-hi); }
.ts-nums i, .ts-stats i { font-style: normal; color: var(--brass); font-family: var(--cond); font-weight: 600; font-size: 11.5px; letter-spacing: .3px; }
.ts-nums i + i, .ts-nums i:not(:first-child) { margin-left: 4px; }
.ts-line { grid-column: 3 / span 2; min-width: 0; font-size: 11.5px; }
.ts-stats { display: flex; flex-wrap: wrap; gap: 0 9px; color: var(--ink-hi); font-variant-numeric: tabular-nums; }
.ts-kit { color: var(--ink); }
.ts-fx { color: #9fc4e2; }
.ts-skills { display: flex; flex-wrap: wrap; gap: 2px 8px; color: var(--mute); }
.ts-sk { display: inline-flex; align-items: center; gap: 3px; }
.ts-rar { font-style: normal; padding: 0 4px; border-radius: 3px; font-size: 10.5px; line-height: 14px; color: #fff; text-shadow: 0 1px 0 rgba(0,0,0,.5); }
.ts-empty { margin: 4px 0; font-size: 12.5px; color: var(--mute); font-style: italic; }
</style>
